import React, { useState, useEffect, useMemo } from 'react';
import { getApiBase } from '../api';

function money(cents) {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const statusStyles = {
  PENDING: { bg: '#fef3c7', text: '#92400e', label: '⏳ Pending' },
  APPROVED: { bg: '#dbeafe', text: '#1e40af', label: '✓ Approved' },
  SCHEDULED: { bg: '#e0e7ff', text: '#3730a3', label: '📅 Scheduled' },
  PAID: { bg: '#d1fae5', text: '#065f46', label: '✅ Paid' },
  VOID: { bg: '#e5e7eb', text: '#6b7280', label: '🚫 Void' }
};

export default function AccountingSettlements() {
  const apiBase = useMemo(() => getApiBase(), []);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [payments, setPayments] = useState([]);
  const [unsettledLoads, setUnsettledLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('PENDING');
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedPayments, setSelectedPayments] = useState(new Set());

  useEffect(() => {
    fetchData();
  }, [activeTab, filter]);

  async function fetchData() {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const res = await fetch(`${apiBase}/invoicing/dashboard`, {
          headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
        });
        if (res.ok) setDashboard(await res.json());
      } else if (activeTab === 'payments') {
        const res = await fetch(`${apiBase}/invoicing/carrier-payments?status=${filter}`, {
          headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
        });
        if (res.ok) {
          const data = await res.json();
          setPayments(data.payments || []);
        }
      } else if (activeTab === 'unsettled') {
        const res = await fetch(`${apiBase}/invoicing/loads/unsettled`, {
          headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
        });
        if (res.ok) {
          const data = await res.json();
          setUnsettledLoads(data.loads || []);
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function createPaymentFromLoad(loadId) {
    setActionLoading(loadId);
    try {
      const res = await fetch(`${apiBase}/invoicing/carrier-payments/from-load/${loadId}`, {
        method: 'POST',
        headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: `Payment ${data.paymentNumber} created!` });
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  }

  async function updatePaymentStatus(paymentId, status, extra = {}) {
    setActionLoading(paymentId);
    try {
      const res = await fetch(`${apiBase}/invoicing/carrier-payments/${paymentId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-user-role': 'ACCOUNTING_ADMIN' },
        body: JSON.stringify({ status, ...extra })
      });
      if (res.ok) {
        setMessage({ type: 'success', text: `Payment ${status.toLowerCase()}` });
        fetchData();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  }

  async function createBatch() {
    if (selectedPayments.size === 0) {
      setMessage({ type: 'error', text: 'Select payments to batch' });
      return;
    }
    
    setActionLoading('batch');
    try {
      const res = await fetch(`${apiBase}/invoicing/batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-role': 'ACCOUNTING_ADMIN' },
        body: JSON.stringify({ 
          paymentMethod: 'ACH',
          paymentIds: Array.from(selectedPayments)
        })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: `Batch ${data.batchNumber} created with ${selectedPayments.size} payments!` });
        setSelectedPayments(new Set());
        fetchData();
      } else {
        setMessage({ type: 'error', text: data.error });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(null);
    }
  }

  function togglePaymentSelection(paymentId) {
    const newSelected = new Set(selectedPayments);
    if (newSelected.has(paymentId)) {
      newSelected.delete(paymentId);
    } else {
      newSelected.add(paymentId);
    }
    setSelectedPayments(newSelected);
  }

  function selectAllApproved() {
    const approvedIds = payments.filter(p => p.status === 'APPROVED').map(p => p.id);
    setSelectedPayments(new Set(approvedIds));
  }

  const tabs = [
    { id: 'dashboard', label: '📊 Dashboard' },
    { id: 'payments', label: '💳 Payments' },
    { id: 'unsettled', label: '📦 Unsettled Loads' },
    { id: 'aging', label: '📈 AP Aging' }
  ];

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>🚛 Carrier Settlements & Accounts Payable</h1>
        <p style={{ margin: '8px 0 0', color: '#6b7280' }}>
          Manage carrier payments and settlements
        </p>
      </div>

      {message && (
        <div style={{
          padding: 12,
          marginBottom: 16,
          borderRadius: 8,
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {message.text}
          <button onClick={() => setMessage(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #e5e7eb', paddingBottom: 12 }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSelectedPayments(new Set()); }}
            style={{
              padding: '10px 20px',
              background: activeTab === tab.id ? '#059669' : 'white',
              color: activeTab === tab.id ? 'white' : '#374151',
              border: activeTab === tab.id ? 'none' : '1px solid #d1d5db',
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>}

      {/* Dashboard Tab */}
      {!loading && activeTab === 'dashboard' && dashboard && (
        <div>
          {/* Stats Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <StatCard label="Pending Approval" value={dashboard.carrierPayments.pending} color="#f59e0b" subtext={money(dashboard.carrierPayments.pendingPayoutCents)} />
            <StatCard label="Ready to Pay" value={dashboard.carrierPayments.approved} color="#2563eb" subtext="Approved" />
            <StatCard label="Scheduled" value={dashboard.carrierPayments.scheduled} color="#7c3aed" subtext="In batch" />
            <StatCard label="Paid (30d)" value={money(dashboard.carrierPayments.paid30DaysCents)} color="#059669" subtext="Settled" />
          </div>

          {/* Recent Payments */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
            <h3 style={{ margin: '0 0 16px' }}>Recent Carrier Payments</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Payment #</th>
                  <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Carrier</th>
                  <th style={{ padding: 10, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Rate</th>
                  <th style={{ padding: 10, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Net Pay</th>
                  <th style={{ padding: 10, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recentPayments?.map(pay => {
                  const style = statusStyles[pay.status] || statusStyles.PENDING;
                  return (
                    <tr key={pay.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: 10, fontWeight: 600 }}>{pay.paymentNumber}</td>
                      <td style={{ padding: 10 }}>{pay.carrierName}</td>
                      <td style={{ padding: 10, textAlign: 'right' }}>{money(pay.rateAmountCents)}</td>
                      <td style={{ padding: 10, textAlign: 'right', fontWeight: 600, color: '#059669' }}>{money(pay.netPayCents)}</td>
                      <td style={{ padding: 10, textAlign: 'center' }}>
                        <span style={{ padding: '4px 12px', borderRadius: 20, background: style.bg, color: style.text, fontSize: 12, fontWeight: 600 }}>
                          {style.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payments Tab */}
      {!loading && activeTab === 'payments' && (
        <div>
          {/* Filters & Batch Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {['PENDING', 'APPROVED', 'SCHEDULED', 'PAID', 'ALL'].map(status => (
                <button
                  key={status}
                  onClick={() => { setFilter(status); setSelectedPayments(new Set()); }}
                  style={{
                    padding: '8px 16px',
                    background: filter === status ? '#059669' : '#f3f4f6',
                    color: filter === status ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: 20,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: 13
                  }}
                >
                  {status}
                </button>
              ))}
            </div>
            
            {filter === 'APPROVED' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={selectAllApproved}
                  style={{ padding: '8px 16px', background: '#f3f4f6', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                >
                  Select All
                </button>
                <button
                  onClick={createBatch}
                  disabled={selectedPayments.size === 0 || actionLoading === 'batch'}
                  style={{
                    padding: '8px 20px',
                    background: selectedPayments.size > 0 ? '#7c3aed' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: selectedPayments.size > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: 600
                  }}
                >
                  📦 Create Batch ({selectedPayments.size})
                </button>
              </div>
            )}
          </div>

          {/* Payments Table */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {filter === 'APPROVED' && <th style={{ padding: 12, width: 40, borderBottom: '1px solid #e5e7eb' }}></th>}
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Payment #</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Carrier</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Load</th>
                  <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Rate</th>
                  <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Deductions</th>
                  <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Net Pay</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={filter === 'APPROVED' ? 9 : 8} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No payments found</td></tr>
                ) : payments.map(pay => {
                  const style = statusStyles[pay.status] || statusStyles.PENDING;
                  const deductions = (pay.factoringFeeCents || 0) + (pay.deductionsCents || 0);
                  return (
                    <tr key={pay.id} style={{ borderBottom: '1px solid #f3f4f6', background: selectedPayments.has(pay.id) ? '#f0fdf4' : 'white' }}>
                      {filter === 'APPROVED' && (
                        <td style={{ padding: 12, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedPayments.has(pay.id)}
                            onChange={() => togglePaymentSelection(pay.id)}
                            style={{ width: 18, height: 18, cursor: 'pointer' }}
                          />
                        </td>
                      )}
                      <td style={{ padding: 12, fontWeight: 600 }}>{pay.paymentNumber}</td>
                      <td style={{ padding: 12 }}>{pay.carrierName}</td>
                      <td style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>
                        {pay.originCity && pay.destCity ? `${pay.originCity}, ${pay.originState} → ${pay.destCity}, ${pay.destState}` : pay.loadReference || '—'}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right' }}>{money(pay.rateAmountCents)}</td>
                      <td style={{ padding: 12, textAlign: 'right', color: deductions > 0 ? '#dc2626' : '#6b7280' }}>
                        {deductions > 0 ? `-${money(deductions)}` : '—'}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{money(pay.netPayCents)}</td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <span style={{ padding: '4px 12px', borderRadius: 20, background: style.bg, color: style.text, fontSize: 12, fontWeight: 600 }}>
                          {style.label}
                        </span>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          {pay.status === 'PENDING' && (
                            <button
                              onClick={() => updatePaymentStatus(pay.id, 'APPROVED')}
                              disabled={actionLoading === pay.id}
                              style={{ padding: '4px 10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
                            >
                              Approve
                            </button>
                          )}
                          {pay.status === 'APPROVED' && (
                            <button
                              onClick={() => updatePaymentStatus(pay.id, 'PAID', { paymentMethod: 'ACH' })}
                              disabled={actionLoading === pay.id}
                              style={{ padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
                            >
                              Pay Now
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unsettled Loads Tab */}
      {!loading && activeTab === 'unsettled' && (
        <div>
          <div style={{ marginBottom: 16, padding: 12, background: '#fef3c7', borderRadius: 8, border: '1px solid #f59e0b' }}>
            <strong>⚠️ {unsettledLoads.length} delivered loads need carrier payment records</strong>
          </div>

          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Load</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Carrier</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Route</th>
                  <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Carrier Rate</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Delivered</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {unsettledLoads.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>🎉 All delivered loads have payment records!</td></tr>
                ) : unsettledLoads.map(load => (
                  <tr key={load.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 12, fontWeight: 600 }}>{load.referenceNumber || load.id.slice(-8)}</td>
                    <td style={{ padding: 12 }}>{load.carrierName}</td>
                    <td style={{ padding: 12, fontSize: 13 }}>
                      {load.originCity}, {load.originState} → {load.destCity}, {load.destState}
                    </td>
                    <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>{money(load.carrierRateCents || load.rateAmountCents)}</td>
                    <td style={{ padding: 12, textAlign: 'center', fontSize: 13 }}>{formatDate(load.updatedAt)}</td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => createPaymentFromLoad(load.id)}
                        disabled={actionLoading === load.id}
                        style={{
                          padding: '6px 14px',
                          background: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 600,
                          cursor: actionLoading === load.id ? 'not-allowed' : 'pointer',
                          opacity: actionLoading === load.id ? 0.5 : 1
                        }}
                      >
                        {actionLoading === load.id ? 'Creating...' : '💳 Create Payment'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AP Aging Tab */}
      {!loading && activeTab === 'aging' && <APAgingReport apiBase={apiBase} />}
    </div>
  );
}

function StatCard({ label, value, color, subtext }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', padding: 20 }}>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      {subtext && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{subtext}</div>}
    </div>
  );
}

function APAgingReport({ apiBase }) {
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAging() {
      try {
        const res = await fetch(`${apiBase}/invoicing/reports/ap-aging`, {
          headers: { 'x-user-role': 'ACCOUNTING_ADMIN' }
        });
        if (res.ok) setAging(await res.json());
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchAging();
  }, [apiBase]);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}>Loading aging report...</div>;
  if (!aging) return <div>Error loading report</div>;

  const money = (cents) => `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  return (
    <div>
      <div style={{ marginBottom: 16, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #10b981' }}>
        <strong>💡 Carrier Payment Aging</strong>
        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#065f46' }}>
          Industry standard is to pay carriers within 21 days of delivery. QuickPay options provide same-day or next-day payment for a small fee.
        </p>
      </div>

      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Carrier</th>
              <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Payments</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>0-7 Days</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>8-14 Days</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>15-21 Days</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb', color: '#dc2626' }}>21+ Days</th>
              <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 700 }}>Total Owed</th>
            </tr>
          </thead>
          <tbody>
            {aging.aging?.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No outstanding carrier payments</td></tr>
            ) : aging.aging?.map(row => {
              const total = Number(row.days0to7Cents || 0) + Number(row.days8to14Cents || 0) + Number(row.days15to21Cents || 0) + Number(row.over21Cents || 0);
              return (
                <tr key={row.carrierId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: 12, fontWeight: 600 }}>{row.carrierName}</td>
                  <td style={{ padding: 12, textAlign: 'center' }}>{row.paymentCount}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{money(row.days0to7Cents)}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{money(row.days8to14Cents)}</td>
                  <td style={{ padding: 12, textAlign: 'right' }}>{money(row.days15to21Cents)}</td>
                  <td style={{ padding: 12, textAlign: 'right', color: Number(row.over21Cents) > 0 ? '#dc2626' : 'inherit', fontWeight: Number(row.over21Cents) > 0 ? 700 : 400 }}>
                    {money(row.over21Cents)}
                  </td>
                  <td style={{ padding: 12, textAlign: 'right', fontWeight: 700 }}>{money(total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
